import * as basket from "../eaiws/basket";
import { clone } from "../utils/array";
import { ERR_INVALID_ARGUMENT, newError } from "../utils/error";
import { isNotNullOrEmpty, isNullOrEmpty } from "../utils/string";
import { ItemUpdateFlags, BaseItem } from "./BaseItem";
import { BASKET_COLUMN_ID_EXT_ITEM_NUMBER, BASKET_COLUMN_ID_USER_IMAGE, BASKET_COLUMN_ID_COMMENTS, BASKET_COLUMN_ID_ADDITIONAL_IMAGES } from "./Constants";
export class BasketItem extends BaseItem {
    get basket() {
        return (this.mManager);
    }
    /**
     * Returns the set if this item is part of a set.
     */
    getSetArticle() {
        return this.internalGetSetArticle();
    }
    /**
     * Sets the value a text manager text row with the given id.
     * @param pTextId Id of the text manager text
     * @param pValue New value of the text
     * @param pLanguage Which language to set. If undefined the current primary project language will be used.
     */
    async setTmText(pTextId, pValue, pLanguage) {
        let tLang = pLanguage;
        //get project language
        if (isNullOrEmpty(tLang)) {
            const tLangs = await this.basket.projectInfo.getEffectiveProjectLanguages();
            tLang = tLangs[0];
        }
        await this.mManager.session.basket.tmSetText(null, this.id, pTextId, tLang, pValue);
        let tFlags = ItemUpdateFlags.ItemProps;
        //TODO: implement generic check for short text, in this case we have to update the item label
        if (pTextId === "10")
            tFlags = tFlags | ItemUpdateFlags.Item;
        return (this.updateItem(tFlags));
    }
    /**
     * Sets the user defined image of this item.
     * @param pUrl Url of the image (supported formats: jpg and png). If `pImportIntoSession` is true this url has to
     * be an HTTP URL previously returned by the `getUploadURL()` eaiws operation and has to include a file extension.
     * Null or an empty string can be used to remove the current user image.
     * @param pImportIntoSession If true the image will be imported into session, and thus will be embedded inside the obk. (default: true)
     */
    async setUserImageUrl(pUrl, pImportIntoSession = true) {
        let tImpUrl = pUrl;
        if (pImportIntoSession && isNotNullOrEmpty(tImpUrl)) {
            tImpUrl = await this.mManager.session.session.importFile(tImpUrl);
        }
        const tField = new basket.ItemField();
        tField.columnId = BASKET_COLUMN_ID_USER_IMAGE;
        tField.itemId = this.id;
        tField.data = tImpUrl == null ? "" : tImpUrl;
        await this.mManager.session.basket.setItemFields([tField]);
        return (this.updateItem(ItemUpdateFlags.UserImage));
    }
    /**
     * Sets an additional user defined image of this item.
     * @param pIndex Index of the image to set (range: [0,9])
     * @param pUrl Url of the image (supported formats: jpg and png). If `pImportIntoSession` is true this url has to
     * be an HTTP URL previously returned by the `getUploadURL()` eaiws operation and has to include a file extension.
     * @param pImportIntoSession If true the image will be imported into session, and thus will be embedded inside the obk. (default: true)
     *
     * Note: Setting the url to null or empty will not delete the image its just set to `null`. Use `deleteAdditionalImage` function instead.
     */
    async setAdditionalImageUrl(pIndex, pUrl, pImportIntoSession = true) {
        if (pIndex < 0 || pIndex > (BASKET_COLUMN_ID_ADDITIONAL_IMAGES.length - 1)) {
            throw newError(ERR_INVALID_ARGUMENT, "index out of bounds");
        }
        let tImpUrl = pUrl;
        if (pImportIntoSession && isNotNullOrEmpty(tImpUrl)) {
            tImpUrl = await this.mManager.session.session.importFile(tImpUrl);
        }
        const tField = new basket.ItemField();
        tField.columnId = BASKET_COLUMN_ID_ADDITIONAL_IMAGES[pIndex];
        tField.itemId = this.id;
        tField.data = tImpUrl == null ? "" : tImpUrl;
        await this.mManager.session.basket.setItemFields([tField]);
        return (this.updateItem(ItemUpdateFlags.AdditionalImages));
    }
    /**
     * Deletes the additional image with the given index and adjusts the index of the following images.
     */
    async deleteAdditionalImage(pIndex) {
        if (pIndex < 0 || pIndex > (BASKET_COLUMN_ID_ADDITIONAL_IMAGES.length - 1)) {
            throw newError(ERR_INVALID_ARGUMENT, "index out of bounds");
        }
        const tRawImages = await this.getRawAdditionalImageUrls(false);
        const tNewImages = clone(tRawImages);
        tNewImages.splice(pIndex, 1);
        const tFields = new Array();
        for (let i = 0; i < tRawImages.length; ++i) { //use length of original array here to reset last image
            const tData = tNewImages[i];
            const tField = new basket.ItemField();
            tField.columnId = BASKET_COLUMN_ID_ADDITIONAL_IMAGES[i];
            tField.itemId = this.id;
            tField.data = tData == null ? "" : tData;
            tFields.push(tField);
        }
        await this.mManager.session.basket.setItemFields(tFields);
        return (this.updateItem(ItemUpdateFlags.AdditionalImages));
    }
    /**
     * Moves an additional image to a new position while adjusting the position of the other images.
     * Note: As a special case `-1` can be used as index to address the `UserImage`.
     */
    async moveAdditionalImage(pFromIndex, pToIndex) {
        if (pFromIndex < -1 || pFromIndex > (BASKET_COLUMN_ID_ADDITIONAL_IMAGES.length - 1)) {
            throw newError(ERR_INVALID_ARGUMENT, "pFromIndex out of bounds");
        }
        if (pToIndex < -1 || pToIndex > (BASKET_COLUMN_ID_ADDITIONAL_IMAGES.length - 1)) {
            throw newError(ERR_INVALID_ARGUMENT, "pToIndex out of bounds");
        }
        if (pFromIndex === pToIndex)
            return;
        const tRawImages = await this.getRawAdditionalImageUrls(true);
        const tNewImages = clone(tRawImages);
        //we have to add 1 to the index because of the user image (index -1)
        const tFromIndex = pFromIndex + 1;
        const tToIndex = pToIndex + 1;
        const tImage = tNewImages[tFromIndex];
        tNewImages.splice(tFromIndex, 1);
        tNewImages.splice(tToIndex, 0, tImage);
        if (tToIndex === 0) { //image moved to user image
            if (tNewImages[1] == null) { //user image was empty before, we do not want to have an empty additional image -> remove it
                tNewImages.splice(1, 1);
            }
        }
        const tFields = new Array();
        for (let i = 0; i < tRawImages.length; ++i) { //use length of original array here to reset last image
            const tData = tNewImages[i];
            const tField = new basket.ItemField();
            tField.columnId = i === 0 ? BASKET_COLUMN_ID_USER_IMAGE : BASKET_COLUMN_ID_ADDITIONAL_IMAGES[i - 1];
            tField.itemId = this.id;
            tField.data = tData == null ? "" : tData;
            tFields.push(tField);
        }
        await this.mManager.session.basket.setItemFields(tFields);
        let tFlags = ItemUpdateFlags.AdditionalImages;
        if (pFromIndex === -1 || pToIndex === -1) {
            tFlags |= ItemUpdateFlags.UserImage;
        }
        return (this.updateItem(tFlags));
    }
    /**
     * Sets the external item number of this item.
     */
    async setExtItemNumber(pExtItemNo) {
        const tField = new basket.ItemField();
        tField.columnId = BASKET_COLUMN_ID_EXT_ITEM_NUMBER;
        tField.itemId = this.id;
        tField.data = pExtItemNo;
        await this.mManager.session.basket.setItemFields([tField]);
        return (this.updateItem(ItemUpdateFlags.ExtItemNumber));
    }
    /**
     * Sets comments for this item.
     */
    async setComments(pComments) {
        const tField = new basket.ItemField();
        tField.columnId = BASKET_COLUMN_ID_COMMENTS;
        tField.itemId = this.id;
        tField.data = pComments;
        await this.mManager.session.basket.setItemFields([tField]);
        return (this.updateItem(ItemUpdateFlags.Comments));
    }
    //// protected /////////////////////////////////////////////////////////////////////////////////
    get asType() {
        return (this);
    }
    registerItem() {
        this.mManager._registerItem(this);
    }
    static async BuildDisplayText(pBasket, pText) {
        const tLangs = await pBasket.projectInfo.getEffectiveProjectLanguages();
        const tText = new basket.DisplayText();
        tText.value = pText.trim();
        tText.lang = tLangs[0];
        return (tText);
    }
    async performUpdates(pFlags) {
        await super.performUpdates(pFlags);
        return (this.mManager._updateViewItems(this, pFlags));
    }
    //// private ///////////////////////////////////////////////////////////////////////////////////
    async getRawAdditionalImageUrls(pIncludeUserImage) {
        const tViewId = this.mManager._getViewId();
        const tOptions = new basket.GetItemFieldsOptions();
        tOptions.resolveURI = false;
        if (tViewId != null)
            tOptions.viewId = tViewId;
        const tImageUrls = new Array();
        const tFieldMap = new Map();
        let tColumns = BASKET_COLUMN_ID_ADDITIONAL_IMAGES;
        if (pIncludeUserImage) {
            tColumns = new Array(BASKET_COLUMN_ID_USER_IMAGE, ...BASKET_COLUMN_ID_ADDITIONAL_IMAGES);
        }
        const tFields = await this.mManager.session.basket.getItemFields([this.id], tColumns, tOptions);
        for (const tField of tFields) {
            tFieldMap.set(tField.columnId, tField.data);
        }
        if (pIncludeUserImage) {
            tImageUrls[0] = tFieldMap.get(BASKET_COLUMN_ID_USER_IMAGE);
        }
        for (let i = 0; i < BASKET_COLUMN_ID_ADDITIONAL_IMAGES.length; ++i) {
            const tImage = tFieldMap.get(BASKET_COLUMN_ID_ADDITIONAL_IMAGES[i]);
            if (tImage != null) { //only assign if valid to get correct array length
                const tIndex = pIncludeUserImage ? i + 1 : i;
                tImageUrls[tIndex] = tImage;
            }
        }
        return (tImageUrls);
    }
}
